import React, { useState, useEffect } from 'react';
import {
  Dialog,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  alpha,
  CircularProgress,
  useMediaQuery,
  useTheme as useMuiTheme,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Avatar,
  Chip,
  Divider,
} from '@mui/material';
import {
  Close,
  Flag,
  Warning,
  Block,
  Report,
  Group,
  Diversity3,
  Gavel,
  Public,
} from '@mui/icons-material';
import { useTheme as useCustomTheme } from '../../context/ThemeContext';
import toast from 'react-hot-toast';
import { communityApi, ReportCommunityData } from '../../services/communityApi';

// Import your logo
import logo from '../../assets/Text-WFHCafe_WObg.png';

interface ReportCommunityModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  community: {
    id: string;
    name: string;
    description: string;
    owner: {
      id: string;
      username: string;
      profilePicture?: string;
    };
    membersCount: number;
    createdAt: string;
    slug: string;
    profilePicture?: string;
    bannerImage?: string;
  };
}

const ReportCommunityModal: React.FC<ReportCommunityModalProps> = ({
  open,
  onClose,
  onSuccess,
  community
}) => {
  const muiTheme = useMuiTheme();
  const { mode } = useCustomTheme();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    reportType: '',
    customReason: '',
    description: '',
  });

  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(max-width:900px)');
  const isDark = mode === 'dark';
  const goldColor = '#C4B66F';
  const deepGreen = '#051814';
  const lokiGradient = 'linear-gradient(135deg, #051814 0%, #0F3B2E 100%)';
  const goldGradient = `linear-gradient(135deg, ${goldColor} 0%, #D4C690 100%)`;

  // Glass effect styling
  const glassEffect = {
    backdropFilter: 'blur(20px)',
    background: isDark
      ? 'linear-gradient(135deg, rgba(5, 24, 20, 0.98) 0%, rgba(15, 59, 46, 0.98) 100%)'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 250, 0.98) 100%)',
    border: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
    boxShadow: isDark
      ? `0 25px 50px ${alpha('#000000', 0.5)}`
      : `0 25px 50px ${alpha(deepGreen, 0.15)}`,
  };

  const modalStyle = {
    borderRadius: '16px',
    ...glassEffect,
    width: isMobile ? 'calc(100vw - 32px)' : (isTablet ? '600px' : '700px'),
    maxHeight: isMobile ? 'calc(100vh - 300px)' : '80vh',
    margin: 'auto',
    overflow: 'hidden',
    padding: 0,
    '& .MuiDialogContent-root': {
      overflowY: 'auto',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
      msOverflowStyle: 'none',
      scrollbarWidth: 'none',
    },
  };

  // Input field styles with gold focus border
  const inputFieldStyle = {
    borderRadius: '12px',
    bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(5, 24, 20, 0.02)',
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(5, 24, 20, 0.1)',
        borderWidth: '1px',
        borderRadius: '12px'
      },
      '&:hover fieldset': { 
        borderColor: goldColor,
        borderWidth: '1px'
      },
      '&.Mui-focused fieldset': { 
        borderColor: goldColor,
        borderWidth: '2px'
      }
    },
    '& .MuiInputLabel-root': {
      color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)',
      fontWeight: 600
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: goldColor,
      fontWeight: 700
    }
  };

  const gradientButtonStyle = {
    background: isDark ? goldGradient : lokiGradient,
    color: isDark ? deepGreen : goldColor,
    borderRadius: '50px',
    fontWeight: 800,
    textTransform: 'none',
    fontSize: isMobile ? '0.9rem' : '1rem',
    padding: isMobile ? '10px 20px' : '12px 32px',
    letterSpacing: '-0.02em',
    border: `1px solid ${alpha(isDark ? goldColor : deepGreen, 0.2)}`,
    boxShadow: isDark
      ? `0 8px 32px ${alpha(goldColor, 0.25)}`
      : `0 8px 32px ${alpha(deepGreen, 0.2)}`,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: isDark
        ? `0 12px 40px ${alpha(goldColor, 0.35)}`
        : `0 12px 40px ${alpha(deepGreen, 0.3)}`,
      background: isDark
        ? `linear-gradient(135deg, ${alpha(goldColor, 0.9)} 0%, #E5D9A8 100%)`
        : 'linear-gradient(135deg, #051814 0%, #1D5C4A 100%)',
    },
    '&:active': {
      transform: 'translateY(0)',
    }
  };

  const reportTypes = [
    { value: 'inappropriate_content', label: 'Inappropriate content', icon: <Warning /> },
    { value: 'harassment', label: 'Harassment or bullying', icon: <Block /> },
    { value: 'hate_speech', label: 'Hate speech or discrimination', icon: <Flag /> },
    { value: 'spam', label: 'Spam or fake community', icon: <Group /> },
    { value: 'privacy_violation', label: 'Privacy violation', icon: <Diversity3 /> },
    { value: 'community_guidelines', label: 'Violates community guidelines', icon: <Gavel /> },
    { value: 'copyright', label: 'Copyright infringement', icon: <Public /> },
    { value: 'other', label: 'Other issue', icon: <Warning /> },
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (e: any) => {
    setFormData(prev => ({
      ...prev,
      reportType: e.target.value
    }));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleSubmit = async () => {
    if (!formData.reportType) {
      toast.error('Please select a report reason');
      return;
    }

    if (formData.description.length > 1000) {
      toast.error('Description must be 1000 characters or less');
      return;
    }

    setLoading(true);

    try {
      const reportData: ReportCommunityData = {
        reportType: formData.reportType,
        customReason: formData.reportType === 'other' ? formData.customReason : '',
        description: formData.description,
        communityName: community.name,
        communityOwnerId: community.owner.id,
        communityOwnerUsername: community.owner.username,
      };

      const response = await communityApi.reportCommunity(community.id, reportData);

      if (response.success) {
        toast.success('Community report submitted successfully! Our team will review it shortly.');
        onSuccess();
        handleClose();
      } else {
        toast.error(response.error || 'Failed to submit report');
      }
    } catch (error: any) {
      console.error('Report community error:', error);
      
      // Check for duplicate report error
      if (error.response?.status === 400) {
        const errorMessage = error.response?.data?.error || error.message;
        
        if (errorMessage.includes('already reported') || errorMessage.includes('duplicate')) {
          toast.error(
            'You have already reported this community recently. Please wait 24 hours before submitting another report.',
            { duration: 5000 }
          );
        } else if (errorMessage.includes('validation') || errorMessage.includes('required')) {
          toast.error('Please fill in all required fields correctly.');
        } else {
          toast.error(errorMessage || 'Failed to submit report. Please try again.');
        }
      } else if (error.response?.status === 401) {
        toast.error('Please login to report communities.');
      } else if (error.response?.status === 404) {
        toast.error('Community not found. It may have been deleted.');
      } else if (error.response?.status === 429) {
        toast.error('Too many report attempts. Please try again later.');
      } else {
        toast.error(error.message || 'Failed to submit report. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      reportType: '',
      customReason: '',
      description: '',
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: modalStyle,
      }}
      scroll="paper"
    >
      <Box sx={{
        p: isMobile ? 3 : 4,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
      }}>
        {/* Logo and Close Button */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: isMobile ? 3 : 4,
        }}>
          <Box
            component="img"
            src={logo}
            alt="WFHCafe Logo"
            sx={{
              height: isMobile ? 35 : 45,
              width: 'auto',
              filter: isDark ? 'brightness(1.1)' : 'none',
            }}
          />
          
          <IconButton 
            onClick={handleClose} 
            sx={{ 
              color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
              backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              padding: isMobile ? '6px' : '8px',
              '&:hover': { 
                color: goldColor,
                backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(5,24,20,0.1)',
              }
            }}
          >
            <Close fontSize={isMobile ? "medium" : "large"} />
          </IconButton>
        </Box>

        {/* Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
          <Flag sx={{ color: goldColor, fontSize: isMobile ? '2rem' : '2.5rem' }} />
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            sx={{ 
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: isDark ? '#fff' : deepGreen,
              fontSize: isMobile ? '1.5rem' : '2rem',
            }}
          >
            Report Community
          </Typography>
        </Box>
        
        <Typography 
          variant="body1" 
          sx={{ 
            color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
            mb: isMobile ? 3 : 4,
            fontSize: isMobile ? '0.9rem' : '1rem',
          }}
        >
          Help us keep the platform safe by reporting inappropriate communities
        </Typography>

        {/* Community Info */}
        <Box sx={{
          mb: 4,
          p: 2.5,
          borderRadius: '12px',
          bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(5, 24, 20, 0.02)',
          border: `1px solid ${alpha(goldColor, 0.1)}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
            <Avatar
              src={community.profilePicture}
              sx={{
                width: 50,
                height: 50,
                border: `2px solid ${goldColor}`,
              }}
            >
              <Group />
            </Avatar>
            <Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: 800,
                  color: isDark ? '#fff' : deepGreen,
                  fontSize: '1.1rem',
                }}
              >
                {community.name}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
                  fontSize: '0.9rem',
                }}
              >
                Created by {community.owner.username} • {community.membersCount} members
              </Typography>
            </Box>
          </Box>
          <Typography 
            variant="body2" 
            sx={{ 
              color: isDark ? alpha('#fff', 0.7) : alpha(deepGreen, 0.7),
              fontStyle: 'italic',
            }}
          >
            "{community.description}"
          </Typography>
        </Box>

        {/* Report Form Section */}
        <Typography 
          variant="subtitle1" 
          sx={{ 
            fontWeight: 700,
            color: isDark ? '#fff' : deepGreen,
            mb: 2,
            fontSize: isMobile ? '0.95rem' : '1.1rem',
          }}
        >
          Report Details
        </Typography>

        {/* Report Type Selection */}
        <FormControl fullWidth sx={{ mb: 3, ...inputFieldStyle }}>
          <InputLabel sx={{ 
            color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)',
            fontWeight: 600 
          }}>
            Select Report Reason
          </InputLabel>
          <Select
            value={formData.reportType}
            onChange={handleSelectChange}
            label="Select Report Reason"
            sx={{
              color: isDark ? '#fff' : deepGreen,
              '& .MuiSelect-icon': {
                color: goldColor,
              },
              '& .MuiSelect-select': {
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }
            }}
            renderValue={(selected) => {
              const selectedType = reportTypes.find(type => type.value === selected);
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {selectedType?.icon}
                  <span>{selectedType?.label}</span>
                </Box>
              );
            }}
          >
            {reportTypes.map((type) => (
              <MenuItem 
                key={type.value} 
                value={type.value}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  color: isDark ? '#fff' : deepGreen,
                  '&:hover': {
                    bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(5,24,20,0.05)',
                  }
                }}
              >
                <Box sx={{ color: goldColor, display: 'flex', alignItems: 'center' }}>
                  {type.icon}
                </Box>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Custom Reason (if "Other" is selected) */}
        {formData.reportType === 'other' && (
          <TextField
            fullWidth
            label="Please specify the reason"
            name="customReason"
            value={formData.customReason}
            onChange={handleInputChange}
            sx={{ mb: 3, ...inputFieldStyle }}
            placeholder="Describe the issue..."
            helperText="Please be specific about what's wrong with this community"
            FormHelperTextProps={{
              sx: {
                color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(5,24,20,0.5)',
                fontSize: isMobile ? '0.8rem' : '0.85rem',
              }
            }}
          />
        )}

        {/* Additional Details */}
        <TextField
          fullWidth
          label="Additional Details (Optional)"
          name="description"
          value={formData.description}
          onChange={handleInputChange}
          multiline
          rows={4}
          sx={{ mb: 3, ...inputFieldStyle }}
          placeholder="Provide any additional information that might help us review this report..."
          helperText={`${formData.description.length}/1000 characters`}
          FormHelperTextProps={{
            sx: {
              color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(5,24,20,0.5)',
              fontSize: isMobile ? '0.8rem' : '0.85rem',
            }
          }}
        />

        {/* Note */}
        <Box sx={{
          mb: 3,
          p: 2,
          borderRadius: '8px',
          bgcolor: alpha(isDark ? '#ff4444' : '#ff6b6b', 0.1),
          border: `1px solid ${alpha(isDark ? '#ff4444' : '#ff6b6b', 0.2)}`,
        }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: isDark ? '#ff4444' : '#d32f2f',
              fontSize: '0.85rem',
              fontWeight: 600,
            }}
          >
            ⚠️ Please note: False reporting may result in account restrictions. Only report communities that violate our guidelines.
          </Typography>
        </Box>

        {/* Buttons at Bottom */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mt: 'auto',
          pt: 3,
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          gap: isMobile ? 2 : 0,
        }}>
          <Button
            onClick={handleClose}
            sx={{
              color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(5,24,20,0.7)',
              fontWeight: 700,
              borderRadius: '50px',
              px: isMobile ? 3 : 4,
              py: isMobile ? 0.75 : 1,
              fontSize: isMobile ? '0.9rem' : '1rem',
              textTransform: 'none',
              minWidth: isMobile ? '120px' : 'auto',
              '&:hover': {
                bgcolor: alpha(goldColor, 0.05)
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading || !formData.reportType}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Flag />}
            sx={{
              ...gradientButtonStyle,
              minWidth: isMobile ? '120px' : 'auto',
              '&:disabled': {
                background: isDark ? 'rgba(196, 182, 111, 0.3)' : 'rgba(5, 24, 20, 0.3)',
                color: isDark ? 'rgba(5, 24, 20, 0.5)' : 'rgba(196, 182, 111, 0.5)',
                transform: 'none',
                boxShadow: 'none',
              }
            }}
          >
            {loading ? 'Submitting...' : 'Submit Report'}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ReportCommunityModal;